import React, {useRef} from 'react';
import {Text, Button} from '@/ui-kits';
import {FileSerializableItem} from '@/interface';
import {FilePreview} from '../../../common';
import {cn} from '@/utils/common_functions';
import { supportedExtentions } from '@/constants/defaults';
import { useSelector, useDispatch } from 'react-redux';
import { specialistConsultationAdditionalFilesLoadingStatus } from '@/services/redux/selectors';
import {removeSpecialistConsultationAdditionalFilesRequest} from '@/services/redux/slices';

interface AttachmentsProps {
  isReadonly: boolean;
  files: FileSerializableItem[];
  onUploadFile: (files: File[]) => void;
  case_id?: number;
}

const Attachments: React.FC<AttachmentsProps> = props => {
  const {isReadonly, files, onUploadFile, case_id} = props;
  const consultationAdditionalFilesLoading = useSelector(specialistConsultationAdditionalFilesLoadingStatus);
  const dispatch = useDispatch();

  
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const selectedFiles = event.target.files ?? [];
    onUploadFile(Array.from(selectedFiles));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }

  function handleUploadFile() {
    if (consultationAdditionalFilesLoading) return;
    fileInputRef.current?.click();
  }

  function handleDeleteFile(id: number){
    if (!case_id) return;
    dispatch(removeSpecialistConsultationAdditionalFilesRequest({id: case_id, file_id: Number(id)}));
  }

  return (
    <div className="bg-white rounded-lg border border-ui_input_border_dark/40 p-4 sm:p-6">
      {/* title and upload button */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-0">
        <div>
          <Text variant="title" className="text-ui_blue mb-1 !text-base sm:!text-lg">
            Attachments (Optional)
          </Text>
        </div>
        {!isReadonly && (
          <>
            <input type="file" multiple onChange={handleFileChange} ref={fileInputRef} className="hidden" accept={supportedExtentions.join(',')}/>
            <Button
              title={consultationAdditionalFilesLoading ? '' : 'Upload File'}
              variant="secondary"
              leftIcon="upload_up"
              isLoading={consultationAdditionalFilesLoading}
              className="w-full sm:w-auto px-4 py-2 rounded-lg font-FigtreeMedium !text-xs sm:!text-sm hover:bg-[#EFF6FF] transition-colors flex items-center gap-2"
              iconClassName="size-3"
              onClick={handleUploadFile}
            />
          </>
        )}
      </div>

      <div className={cn(files.length > 0 && 'grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 mt-3 sm:mt-4', 'mt-2')}>
        {files.length === 0 && (
          <Text className="font-FigtreeRegular !text-xs sm:!text-sm text-ui_input_border_dark">
            {isReadonly ? 'No attachments found' : 'Attach any supporting files, reports, or images to support your second opinion'}
          </Text>
        )}
        {files.map(file => (
          <FilePreview readonly={isReadonly} handleDelete={handleDeleteFile} bgVariant='blue' allowed_actions={['delete']} key={file.id} file={file} />
        ))}
      </div>
    </div>
  );
};

export default Attachments;
